// Renderer for plant objects

import * as THREE from 'three'
import type { GardenScene } from './GardenScene'
import { modelLoader } from './ModelLoader'
import { texturePreloader } from './TexturePreloader'
import { elevationService } from './ElevationService'
import { debug } from '@/utils/debug'
import { DPM_SCALE } from '@/constants/coordinates'
import type { Plant, Product, ProductFile } from '@/types'

export class PlantRenderer {
  private scene: GardenScene
  private meshes: Map<string, THREE.Object3D> = new Map()
  private topViewMeshes: Map<string, THREE.Object3D> = new Map()  // Separate tracking for 2D mode visibility toggle
  private products: Map<string, Product> = new Map()
  private topTextureUrls: Map<string, ProductFile[]> = new Map()
  private billboardUrls: Map<string, ProductFile> = new Map()  // Pre-fetched billboard textures by product ID
  private textureLoader: THREE.TextureLoader = new THREE.TextureLoader()
  private billboardTextureCache: Map<string, THREE.Texture> = new Map()
  private currentViewMode: '2d' | '3d' = '2d'

  constructor(scene: GardenScene) {
    this.scene = scene
  }

  /**
   * Render all plants
   * @param plants - Plant objects to render
   * @param modelUrls - GLB model URLs by product ID
   * @param topTextureUrls - Top-view texture URLs by product ID (for 2D mode billboards)
   * @param billboardUrls - Pre-fetched billboard textures by product ID (for 3D fallback)
   * @param products - Product details by product ID
   */
  async renderPlants(
    plants: Plant[],
    modelUrls: Map<string, ProductFile[]>,
    topTextureUrls: Map<string, ProductFile[]> = new Map(),
    billboardUrls: Map<string, ProductFile> = new Map(),
    products: Map<string, Product> = new Map()
  ): Promise<void> {
    this.products = products
    this.topTextureUrls = topTextureUrls
    this.billboardUrls = billboardUrls

    debug.log('Plant', `Top textures available for ${this.topTextureUrls.size} products (for 2D mode)`)

    // Validate scene is available
    if (!this.scene || !this.scene.plantsGroup) {
      throw new Error('PlantRenderer: Scene or plantsGroup is not available')
    }

    // Clear existing plant meshes
    this.clear()

    // Group plants by product for potential instancing
    const plantsByProduct = new Map<string, Plant[]>()
    for (const plant of plants) {
      if (plant.markedForDestruction) continue

      const productId = plant.productId || 'default'
      if (!plantsByProduct.has(productId)) {
        plantsByProduct.set(productId, [])
      }
      plantsByProduct.get(productId)!.push(plant)
    }

    // Render each group
    for (const [productId, productPlants] of plantsByProduct) {
      const files = modelUrls.get(productId)
      const modelUrl = files?.find(f => f.kind === 'glb')?.fileUrl

      if (modelUrl) {
        await this.renderPlantGroup(productPlants, modelUrl)
      } else {
        // No GLB model - use fallback chain
        await this.renderPlantsFallback(productId, productPlants)
      }
    }
  }

  /**
   * Fallback chain when no GLB model is available (or fails to load)
   * Renders both top-view and side-view billboards when available (for eventual view-mode switching)
   * Falls back to cone placeholder if neither is available
   */
  private async renderPlantsFallback(productId: string | undefined, plants: Plant[]): Promise<void> {
    let hasRendered = false

    // 1. Try top-view texture (flat billboard for 2D mode)
    if (productId) {
      const topFiles = this.topTextureUrls.get(productId)
      const topFile = topFiles?.find(f => f.kind === 'top')
      if (topFile) {
        await this.renderPlantGroupTopView(plants, topFile)
        hasRendered = true
      }
    }

    // 2. Try side-view billboard (crossed planes for 3D mode)
    // Render even if top-view exists - both needed for view-mode switching
    if (productId) {
      const billboardFile = this.billboardUrls.get(productId)
      if (billboardFile) {
        await this.renderPlantGroupBillboard(plants, billboardFile)
        hasRendered = true
      }
    }

    // 3. Fall back to cone placeholder if nothing rendered
    if (!hasRendered) {
      debug.log('Plant', `No textures for product ${productId || 'unknown'}, using cone fallback`)
      this.renderPlantGroupFallback(plants)
    }

    // 4. Link top-view billboards to their main meshes (for selection highlighting)
    // When top-view is clicked, we want to highlight the main mesh
    for (const plant of plants) {
      const topViewMesh = this.topViewMeshes.get(plant.id)
      const mainMesh = this.meshes.get(plant.id)
      if (topViewMesh && mainMesh) {
        topViewMesh.userData.mainMesh = mainMesh
      }
    }
  }

  /**
   * Render a group of plants using a GLTF model
   */
  private async renderPlantGroup(plants: Plant[], modelUrl: string): Promise<void> {
    try {
      // Load the model
      await modelLoader.load(modelUrl)

      for (const plant of plants) {
        // Verify scene is still valid after async operations
        if (!this.scene || !this.scene.plantsGroup) {
          debug.warn('Plant', 'Scene became unavailable during plant rendering')
          return
        }

        // Get product dimensions for positioning
        const product = plant.productId ? this.products.get(plant.productId) : undefined

        const mesh = await modelLoader.createInstance(modelUrl)
        this.setupPlantMesh(mesh, plant, product)
        this.scene.plantsGroup.add(mesh)
        this.meshes.set(plant.id, mesh)
      }
    } catch (error) {
      debug.warn('Plant', 'Failed to load plant model, trying fallback', error)
      await this.renderPlantsFallback(plants[0]?.productId, plants)
    }
  }

  /**
   * Render plants without models (simple cone/cylinder shapes)
   */
  private renderPlantGroupFallback(plants: Plant[]): void {
    // Verify scene is available
    if (!this.scene || !this.scene.plantsGroup) {
      debug.warn('Plant', 'Scene unavailable for fallback plant rendering')
      return
    }

    // Create shared geometry and material for efficiency
    const geometry = new THREE.ConeGeometry(0.5, 1.5, 8)
    // Translate geometry so base sits at y=0 (cone is centered by default)
    geometry.translate(0, 0.75, 0)
    const material = new THREE.MeshStandardMaterial({
      color: 0x228b22, // Forest green
      roughness: 0.8
    })

    for (const plant of plants) {
      const product = plant.productId ? this.products.get(plant.productId) : undefined
      const mesh = new THREE.Mesh(geometry, material)
      this.setupPlantMesh(mesh, plant, product)
      this.scene.plantsGroup.add(mesh)
      this.meshes.set(plant.id, mesh)
    }
  }

  /**
   * Render plants using top-view textures (flat horizontal plane)
   * Used for 2D orthographic view where plants are seen from above
   */
  private async renderPlantGroupTopView(
    plants: Plant[],
    topFile: ProductFile
  ): Promise<void> {
    // Verify scene is available
    if (!this.scene || !this.scene.plantsGroup) {
      debug.warn('Plant', 'Scene unavailable for top-view plant rendering')
      return
    }

    try {
      // Load the texture (reuse billboard texture cache)
      const texture = await this.loadBillboardTexture(topFile.fileUrl)

      // Calculate size from texture dimensions or product fallback
      const product = plants[0]?.productId ? this.products.get(plants[0].productId) : undefined
      const widthDots = topFile.width || product?.width || 50
      const heightDots = topFile.height || product?.height || 50
      const widthMeters = widthDots * DPM_SCALE
      const heightMeters = heightDots * DPM_SCALE

      debug.log('Plant', `Top-view size: ${widthDots}x${heightDots} dots = ${widthMeters.toFixed(2)}x${heightMeters.toFixed(2)}m`)

      // Create shared material
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        alphaTest: 0.5,
        side: THREE.DoubleSide
      })

      // Create depth material for shadow casting (respects alpha)
      const depthMaterial = new THREE.MeshDepthMaterial({
        depthPacking: THREE.RGBADepthPacking,
        map: texture,
        alphaTest: 0.5
      })

      // Create shared geometry
      const geometry = new THREE.PlaneGeometry(widthMeters, heightMeters)

      for (const plant of plants) {
        const plantProduct = plant.productId ? this.products.get(plant.productId) : undefined

        const mesh = new THREE.Mesh(geometry, material)
        mesh.castShadow = false  // Side-view billboards handle shadow casting
        mesh.customDepthMaterial = depthMaterial

        // Rotate to lie flat (face up)
        mesh.rotation.x = -Math.PI / 2

        // x,y = center (converted from API top-left on import)
        const centerX = plant.x
        const centerY = plant.y

        const worldX = centerX * DPM_SCALE
        const worldZ = centerY * DPM_SCALE
        // Get elevation from ground/buildings beneath plant
        const effectiveElevation = elevationService.getElevationForPlant(centerX, centerY)
        // Position at plant height for correct depth sorting in 2D orthographic view
        // Taller plants will have higher Y, appearing "on top" of shorter plants
        const plantHeightMeters = (plantProduct?.height || heightDots) * DPM_SCALE
        const worldY = (effectiveElevation * DPM_SCALE) + plantHeightMeters

        mesh.position.set(worldX, worldY, worldZ)

        // Use plant's stored rotation (rotation.z because plane is rotated flat)
        mesh.rotation.z = -plant.rotation * Math.PI / 180

        // Store plant data for selection
        mesh.userData.plantId = plant.id
        mesh.userData.plantData = plant
        mesh.userData.isTopView = true  // Mark for view-mode toggling
        // Reference to main mesh will be set after side-view is created

        // Respect current view mode (top-view only visible in 2D)
        mesh.visible = this.currentViewMode === '2d'

        this.scene.plantsGroup.add(mesh)
        this.topViewMeshes.set(plant.id, mesh)  // Store in separate map for visibility toggle
      }

      debug.success('Plant', `Rendered ${plants.length} plants as top-view billboards`)
    } catch (error) {
      debug.warn('Plant', 'Failed to render top-view, falling back to side billboard', error)
      // Continue fallback chain: try side-view billboard, then cone
      const productId = plants[0]?.productId
      if (productId) {
        const billboardFile = this.billboardUrls.get(productId)
        if (billboardFile) {
          await this.renderPlantGroupBillboard(plants, billboardFile)
          return
        }
      }
      this.renderPlantGroupFallback(plants)
    }
  }

  /**
   * Render plants using billboard textures (cross/imposter technique)
   * Two intersecting planes at 90° create a 3D illusion from any angle
   */
  private async renderPlantGroupBillboard(
    plants: Plant[],
    billboardFile: ProductFile
  ): Promise<void> {
    // Verify scene is available
    if (!this.scene || !this.scene.plantsGroup) {
      debug.warn('Plant', 'Scene unavailable for billboard plant rendering')
      return
    }

    try {
      // Load the texture (with caching)
      const texture = await this.loadBillboardTexture(billboardFile.fileUrl)

      // Calculate size from texture dimensions or product fallback
      const product = plants[0]?.productId ? this.products.get(plants[0].productId) : undefined
      const widthDots = billboardFile.width || product?.width || 50
      const heightDots = billboardFile.height || product?.height || 50
      const widthMeters = widthDots * DPM_SCALE
      const heightMeters = heightDots * DPM_SCALE

      debug.log('Plant', `Billboard size: ${widthDots}x${heightDots} dots = ${widthMeters.toFixed(2)}x${heightMeters.toFixed(2)}m`)

      // Create shared material for efficiency
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        alphaTest: 0.5,  // Alpha cutout for crisp edges
        side: THREE.DoubleSide
      })

      // Create depth material for shadow casting (respects alpha)
      const depthMaterial = new THREE.MeshDepthMaterial({
        depthPacking: THREE.RGBADepthPacking,
        map: texture,
        alphaTest: 0.5
      })

      // Create shared geometry
      const geometry = new THREE.PlaneGeometry(widthMeters, heightMeters)

      for (const plant of plants) {
        const plantProduct = plant.productId ? this.products.get(plant.productId) : undefined
        const group = new THREE.Group()

        // First plane (along X axis)
        const plane1 = new THREE.Mesh(geometry, material)
        plane1.position.y = heightMeters / 2  // Base at ground level
        plane1.castShadow = true
        plane1.customDepthMaterial = depthMaterial

        // Second plane (along Z axis, rotated 90°)
        const plane2 = new THREE.Mesh(geometry, material)
        plane2.rotation.y = Math.PI / 2
        plane2.position.y = heightMeters / 2
        plane2.castShadow = true
        plane2.customDepthMaterial = depthMaterial

        group.add(plane1, plane2)

        // Position the billboard group
        this.setupBillboardPosition(group, plant, plantProduct, widthDots, heightDots)

        this.scene.plantsGroup.add(group)
        this.meshes.set(plant.id, group)
      }

      debug.success('Plant', `Rendered ${plants.length} plants as billboards`)
    } catch (error) {
      debug.warn('Plant', 'Failed to render billboard, falling back to cone', error)
      this.renderPlantGroupFallback(plants)
    }
  }

  /**
   * Load billboard texture with caching
   * Checks preloaded textures first, then local cache, then loads
   */
  private loadBillboardTexture(url: string): Promise<THREE.Texture> {
    // Check preloaded textures first (from TexturePreloader)
    const preloaded = texturePreloader.get(url)
    if (preloaded) {
      return Promise.resolve(preloaded)
    }

    // Check local cache
    const cached = this.billboardTextureCache.get(url)
    if (cached) {
      return Promise.resolve(cached)
    }

    // Load texture (fallback if not preloaded)
    return new Promise((resolve, reject) => {
      this.textureLoader.load(
        url,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace
          this.billboardTextureCache.set(url, texture)
          resolve(texture)
        },
        undefined,
        reject
      )
    })
  }

  /**
   * Setup billboard group position based on plant data
   */
  private setupBillboardPosition(
    group: THREE.Group,
    plant: Plant,
    product: Product | undefined,
    widthDots: number,
    heightDots: number
  ): void {
    // Use product dimensions for center calculation, fallback to billboard dimensions
    const objWidthDots = product?.width || widthDots
    const objDepthDots = product?.height || heightDots

    // x,y = center (converted from API top-left on import)
    const centerX = plant.x
    const centerY = plant.y

    // World coordinates
    const worldX = centerX * DPM_SCALE
    // Get elevation from ground/buildings beneath plant
    const effectiveElevation = elevationService.getElevationForPlant(centerX, centerY)
    const worldY = effectiveElevation * DPM_SCALE
    const worldZ = centerY * DPM_SCALE

    group.position.set(worldX, worldY, worldZ)

    // Use plant's stored rotation (set once at placement, editable by user)
    group.rotation.y = -plant.rotation * Math.PI / 180

    // Store plant data
    group.userData.plantId = plant.id
    group.userData.plantData = plant

    // Add debug label if debug mode is enabled
    if (debug.isEnabled()) {
      const billboardHeightMeters = heightDots * DPM_SCALE
      const labelHeight = billboardHeightMeters + 0.2 // Slightly above billboard

      const label = this.createDebugLabel(
        plant,
        centerX,
        centerY,
        worldX,
        worldZ,
        objWidthDots,
        objDepthDots,
        labelHeight,
        product
      )
      group.add(label)
    }
  }

  /**
   * Setup mesh position, scale, and rotation based on plant data
   */
  private setupPlantMesh(mesh: THREE.Object3D, plant: Plant, product?: Product): void {
    // x,y = center (converted from API top-left on import)
    const centerX = plant.x
    const centerY = plant.y

    // World coordinates
    const worldX = centerX * DPM_SCALE
    // Get elevation from ground/buildings beneath plant
    const effectiveElevation = elevationService.getElevationForPlant(centerX, centerY)
    const worldY = effectiveElevation * DPM_SCALE
    const worldZ = centerY * DPM_SCALE

    mesh.position.set(worldX, worldY, worldZ)

    // Use plant's stored rotation (set once at placement, editable by user)
    mesh.rotation.y = -plant.rotation * Math.PI / 180

    // Enable shadows
    mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true
        child.receiveShadow = true
      }
    })

    // Store plant ID for potential interaction
    mesh.userData.plantId = plant.id
    mesh.userData.plantData = plant

    // Add debug label if debug mode is enabled
    if (debug.isEnabled()) {
      const labelHeight = 1.5 // Default label height above plant
      const widthDots = (product?.width ?? plant.width) || 50
      const depthDots = (product?.height ?? plant.height) || 50

      const label = this.createDebugLabel(
        plant,
        centerX,
        centerY,
        worldX,
        worldZ,
        widthDots,
        depthDots,
        labelHeight,
        product
      )
      mesh.add(label)
    }
  }

  /**
   * Create a debug label sprite showing coordinate information
   */
  private createDebugLabel(
    plant: Plant,
    centerX: number,
    centerY: number,
    worldX: number,
    worldZ: number,
    modelWidthDots: number,
    modelDepthDots: number,
    modelHeight: number,
    product?: Product
  ): THREE.Sprite {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')!
    canvas.width = 280
    canvas.height = 150

    // Background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Border
    ctx.strokeStyle = '#4ecca3'
    ctx.lineWidth = 2
    ctx.strokeRect(1, 1, canvas.width - 2, canvas.height - 2)

    // Text
    ctx.fillStyle = '#ffffff'
    ctx.font = '13px monospace'

    const lines = [
      `${product?.code || plant.productId || 'unknown'}`,
      `pos: (${plant.x.toFixed(0)}, ${plant.y.toFixed(0)})`,
      `size: ${modelWidthDots.toFixed(0)} x ${modelDepthDots.toFixed(0)}`,
      `center: (${centerX.toFixed(0)}, ${centerY.toFixed(0)})`,
      `world: (${worldX.toFixed(2)}, ${worldZ.toFixed(2)})`
    ]

    lines.forEach((line, i) => {
      ctx.fillText(line, 10, 22 + i * 22)
    })

    // Create sprite
    const texture = new THREE.CanvasTexture(canvas)
    const material = new THREE.SpriteMaterial({ map: texture })
    const sprite = new THREE.Sprite(material)

    // Position above the plant based on model height (+ small offset)
    sprite.position.set(0, modelHeight + 0.5, 0)
    sprite.scale.set(2.2, 1.35, 1)

    return sprite
  }

  /**
   * Update a single plant's position/rotation in place (no mesh recreation).
   */
  updatePlant(plant: Plant): void {
    const product = plant.productId ? this.products.get(plant.productId) : undefined
    const centerX = plant.x
    const centerY = plant.y
    const worldX = centerX * DPM_SCALE
    const effectiveElevation = elevationService.getElevationForPlant(centerX, centerY)
    const worldY = effectiveElevation * DPM_SCALE
    const worldZ = centerY * DPM_SCALE
    const rotRad = -plant.rotation * Math.PI / 180

    // Update main mesh (GLB, billboard group, or cone fallback)
    const mainMesh = this.meshes.get(plant.id)
    if (mainMesh) {
      mainMesh.position.set(worldX, worldY, worldZ)
      mainMesh.rotation.y = rotRad
      mainMesh.userData.plantData = plant
    }

    // Update top-view mesh if it exists
    const topMesh = this.topViewMeshes.get(plant.id)
    if (topMesh) {
      const plantHeightMeters = (product?.height || plant.height || 50) * DPM_SCALE
      topMesh.position.set(worldX, (effectiveElevation * DPM_SCALE) + plantHeightMeters, worldZ)
      // Top-view is rotated flat (rotation.x = -PI/2), so Y-rotation maps to Z
      topMesh.rotation.z = rotRad
      topMesh.userData.plantData = plant
    }
  }

  /**
   * Re-query elevation service and update Y positions for all plants.
   * Called after ground shapes change (move/resize) to keep plants at correct height.
   */
  refreshElevations(): void {
    for (const [, mesh] of this.meshes) {
      const plant = mesh.userData.plantData as Plant | undefined
      if (!plant) continue
      this.updatePlant(plant)
    }
  }

  /**
   * Add a single plant without clearing existing ones.
   * Used for incremental placement from the editor.
   */
  async addPlant(
    plant: Plant,
    modelUrls: Map<string, ProductFile[]>,
    products: Map<string, Product> = new Map(),
    topTextureUrls: Map<string, ProductFile[]> = new Map(),
    billboardUrls: Map<string, ProductFile> = new Map()
  ): Promise<void> {
    this.products = products
    // Merge new texture maps into cached ones (don't replace — preserve existing)
    for (const [k, v] of topTextureUrls) {
      if (!this.topTextureUrls.has(k)) this.topTextureUrls.set(k, v)
    }
    for (const [k, v] of billboardUrls) {
      if (!this.billboardUrls.has(k)) this.billboardUrls.set(k, v)
    }
    if (!this.scene || !this.scene.plantsGroup) return

    const productId = plant.productId || 'default'
    const files = modelUrls.get(productId)
    const modelUrl = files?.find(f => f.kind === 'glb')?.fileUrl

    if (modelUrl) {
      try {
        await modelLoader.load(modelUrl)
        const mesh = await modelLoader.createInstance(modelUrl)
        const product = this.products.get(productId)
        this.setupPlantMesh(mesh, plant, product)
        this.scene.plantsGroup.add(mesh)
        this.meshes.set(plant.id, mesh)
      } catch {
        this.renderPlantGroupFallback([plant])
      }
    } else {
      await this.renderPlantsFallback(productId, [plant])
    }
  }

  /**
   * Remove a plant mesh (and its top-view mesh if any)
   */
  removePlant(plantId: string): void {
    const mesh = this.meshes.get(plantId)
    if (mesh) {
      this.scene.plantsGroup.remove(mesh)
      this.meshes.delete(plantId)
    }
    const topMesh = this.topViewMeshes.get(plantId)
    if (topMesh) {
      this.scene.plantsGroup.remove(topMesh)
      this.topViewMeshes.delete(plantId)
    }
  }

  /**
   * Clear all plant meshes
   */
  clear(): void {
    if (this.scene && this.scene.plantsGroup) {
      this.scene.clearGroup(this.scene.plantsGroup)
    }
    this.meshes.clear()
    this.topViewMeshes.clear()
  }

  /**
   * Set view mode - toggles top-view billboard visibility
   * - 2D mode: top-view billboards visible (side billboards always visible for shadows)
   * - 3D mode: top-view billboards hidden (side billboards provide the visual)
   */
  setViewMode(mode: '2d' | '3d'): void {
    this.currentViewMode = mode
    const showTopView = mode === '2d'

    for (const mesh of this.topViewMeshes.values()) {
      mesh.visible = showTopView
    }

    debug.log('Plant', `View mode: ${mode} - top-view billboards ${showTopView ? 'visible' : 'hidden'}`)
  }

  /**
   * Get plant count
   */
  get count(): number {
    return this.meshes.size + this.topViewMeshes.size
  }
}
